import React, { useState, useEffect, useContext } from 'react';
import { Plus, Save, Trash2, Edit2, Star, StarOff, Eye, EyeOff, X } from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { safeUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const font = {
  mono:      '"JetBrains Mono", monospace',
  condensed: '"Barlow Condensed", sans-serif',
  body:      '"Barlow", sans-serif',
};

const EMPTY = {
  name: '', location: '', dates: '', start_date: '', end_date: '', time: '',
  description: '', short_description: '', poster_url: '', ticket_url: '',
  is_featured: false, is_active: true,
  open_to_everyone: true, open_to_everyone_note: '',
};

const S = {
  page:      { padding: '2.5rem', maxWidth: 1000 },
  card:      { background: '#0a0a0a', border: '1px solid #111', padding: '1.5rem', marginBottom: '1rem' },
  secTitle:  { fontFamily: font.mono, fontSize: '0.58rem', color: '#444', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1.25rem' },
  label:     { fontFamily: font.mono, fontSize: '0.55rem', color: '#444', letterSpacing: '0.12em', marginBottom: 5, display: 'block', textTransform: 'uppercase' },
  input:     { background: '#060606', border: '1px solid #1a1a1a', color: '#fff', padding: '0.58rem 0.85rem', fontFamily: font.mono, fontSize: '0.7rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
  textarea:  { background: '#060606', border: '1px solid #1a1a1a', color: '#fff', padding: '0.58rem 0.85rem', fontFamily: font.body, fontSize: '0.82rem', outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6 },
  btnSave:   { background: '#fff', color: '#000', border: 'none', padding: '0.65rem 1.75rem', fontFamily: font.mono, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost:  { background: 'none', border: '1px solid #1a1a1a', color: '#666', padding: '0.3rem 0.65rem', fontFamily: font.mono, fontSize: '0.58rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
  btnDanger: { background: 'none', border: '1px solid #2a1010', color: '#ff6b6b', padding: '0.3rem 0.65rem', fontFamily: font.mono, fontSize: '0.58rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
  check:     { display: 'flex', alignItems: 'center', gap: 8, fontFamily: font.mono, fontSize: '0.6rem', color: '#666', cursor: 'pointer' },
};

// ── Event Form (create or edit) ─────────────────────────────────────────────────

const EventForm = ({ initial = EMPTY, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div style={S.card}>
      <style>{`input:focus, textarea:focus { border-color: #333 !important; }`}</style>
      <div style={S.secTitle}>// {initial.id ? 'Edit Event' : 'New Event'}</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '0.85rem' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={S.label}>Event Name *</label>
          <input style={S.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Con 4 The Culture 2027" />
        </div>
        <div>
          <label style={S.label}>Location</label>
          <input style={S.input} value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Atlanta, GA" />
        </div>
        <div>
          <label style={S.label}>Dates (display text)</label>
          <input style={S.input} value={form.dates} onChange={e => set('dates', e.target.value)} placeholder="e.g. July 23–26, 2026" />
        </div>
        <div>
          <label style={S.label}>Start Date (for sorting)</label>
          <input style={S.input} type="date" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>End Date</label>
          <input style={S.input} type="date" value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Time</label>
          <input style={S.input} value={form.time} onChange={e => set('time', e.target.value)} placeholder="e.g. Thu–Sun · 4:00 PM – 7:00 PM" />
        </div>
        <div>
          <label style={S.label}>Ticket URL</label>
          <input style={S.input} value={form.ticket_url} onChange={e => set('ticket_url', e.target.value)} placeholder="https://..." />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={S.label}>Poster / Flyer Image URL</label>
          <input style={S.input} value={form.poster_url} onChange={e => set('poster_url', e.target.value)} placeholder="https://... or /con4culture-2026.jpg" />
          {form.poster_url && safeUrl(form.poster_url) && (
            <img src={safeUrl(form.poster_url)} alt="preview" style={{ marginTop: 8, height: 80, objectFit: 'cover', border: '1px solid #222' }} onError={e => e.target.style.display = 'none'} />
          )}
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={S.label}>Short Description (shown on grid cards)</label>
          <input style={S.input} value={form.short_description} onChange={e => set('short_description', e.target.value)} placeholder="One sentence summary for the events grid" />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={S.label}>Full Description</label>
          <textarea style={{ ...S.textarea, height: 110 }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Full event description shown on the featured card..." />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={S.label}>"Open to Everyone" Note (leave blank to use default)</label>
          <input style={S.input} value={form.open_to_everyone_note} onChange={e => set('open_to_everyone_note', e.target.value)} placeholder="Custom note, or leave blank for default text" />
        </div>
      </div>

      {/* Toggles */}
      <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <label style={S.check}>
          <input type="checkbox" checked={form.is_featured} onChange={e => set('is_featured', e.target.checked)} style={{ accentColor: '#FFD700' }} />
          FEATURED (shows in hero — max 2)
        </label>
        <label style={S.check}>
          <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} style={{ accentColor: '#fff' }} />
          ACTIVE (visible on site)
        </label>
        <label style={S.check}>
          <input type="checkbox" checked={form.open_to_everyone} onChange={e => set('open_to_everyone', e.target.checked)} style={{ accentColor: '#fff' }} />
          SHOW "OPEN TO EVERYONE" BADGE
        </label>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button style={S.btnSave} onClick={() => onSave(form)} disabled={saving || !form.name.trim()}>
          <Save size={13} />{saving ? 'SAVING...' : 'SAVE EVENT'}
        </button>
        {onCancel && (
          <button style={S.btnGhost} onClick={onCancel}><X size={11} /> CANCEL</button>
        )}
      </div>
    </div>
  );
};

// ── Event Row ────────────────────────────────────────────────────────────────────

const EventRow = ({ event, onEdit, onDelete, onToggleFeatured, onToggleActive }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: '60px 1fr auto',
    gap: '1rem', alignItems: 'center',
    padding: '0.85rem 0', borderBottom: '1px solid #0d0d0d',
    opacity: event.is_active ? 1 : 0.4,
  }}>
    {/* Thumbnail */}
    <div style={{ width: 60, height: 38, background: '#111', overflow: 'hidden', flexShrink: 0 }}>
      {event.poster_url && safeUrl(event.poster_url)
        ? <img src={safeUrl(event.poster_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
        : null
      }
    </div>

    {/* Info */}
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1rem', color: '#ddd', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {event.name}
      </div>
      <div style={{ fontFamily: font.mono, fontSize: '0.55rem', color: '#444' }}>
        {[event.dates, event.location].filter(Boolean).join(' · ')}
        {event.is_featured && <span style={{ marginLeft: 8, color: '#FFD700' }}>★ FEATURED</span>}
        {!event.is_active && <span style={{ marginLeft: 8, color: '#555' }}>HIDDEN</span>}
      </div>
    </div>

    {/* Actions */}
    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
      <button style={{ ...S.btnGhost, color: event.is_featured ? '#FFD700' : '#555', borderColor: event.is_featured ? '#333200' : '#1a1a1a' }} onClick={() => onToggleFeatured(event)} title={event.is_featured ? 'Unfeature' : 'Feature'}>
        {event.is_featured ? <Star size={11} fill="#FFD700" stroke="#FFD700" /> : <StarOff size={11} />}
      </button>
      <button style={S.btnGhost} onClick={() => onToggleActive(event)} title={event.is_active ? 'Hide' : 'Show'}>
        {event.is_active ? <Eye size={11} /> : <EyeOff size={11} />}
      </button>
      <button style={S.btnGhost} onClick={() => onEdit(event)} title="Edit">
        <Edit2 size={11} />
      </button>
      <button style={S.btnDanger} onClick={() => onDelete(event)} title="Delete">
        <Trash2 size={11} />
      </button>
    </div>
  </div>
);

// ── Page ─────────────────────────────────────────────────────────────────────────

const AdminEvents = () => {
  const { authToken }               = useContext(AuthContext);
  const [events,   setEvents]       = useState([]);
  const [loading,  setLoading]      = useState(true);
  const [saving,   setSaving]       = useState(false);
  const [toast,    setToast]        = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [editing,  setEditing]      = useState(null); // event object being edited

  const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/events`, { headers })
      .then(r => r.json())
      .then(d => setEvents(d.events || []))
      .catch(() => showToast('Failed to load events'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form) => {
    if (!form.name.trim()) { showToast('Event name is required'); return; }
    setSaving(true);
    try {
      let res;
      if (form.id) {
        res = await fetch(`${BACKEND_URL}/api/admin/events/${form.id}`, { method: 'PUT', headers, body: JSON.stringify(form) });
      } else {
        res = await fetch(`${BACKEND_URL}/api/admin/events`, { method: 'POST', headers, body: JSON.stringify(form) });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      showToast(`✓ Event ${form.id ? 'updated' : 'created'}`);
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) { showToast(e.message || 'Failed to save event'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Delete "${event.name}"? This cannot be undone.`)) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/events/${event.id}`, { method: 'DELETE', headers });
      setEvents(prev => prev.filter(e => e.id !== event.id));
      showToast('✓ Deleted');
    } catch { showToast('Failed to delete'); }
  };

  const handleToggleFeatured = async (event) => {
    const next = !event.is_featured;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/events/${event.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ ...event, is_featured: next }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      setEvents(prev => prev.map(e => e.id === event.id ? { ...e, is_featured: next } : e));
      showToast(next ? '★ Marked as featured' : 'Removed from featured');
    } catch (e) { showToast(e.message || 'Failed'); }
  };

  const handleToggleActive = async (event) => {
    const next = !event.is_active;
    try {
      await fetch(`${BACKEND_URL}/api/admin/events/${event.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ ...event, is_active: next }),
      });
      setEvents(prev => prev.map(e => e.id === event.id ? { ...e, is_active: next } : e));
      showToast(next ? '✓ Event visible' : 'Event hidden');
    } catch { showToast('Failed'); }
  };

  const featuredCount = events.filter(e => e.is_featured && e.is_active).length;

  return (
    <div style={S.page}>
      <style>{`@keyframes fadein{from{opacity:0}to{opacity:1}}`}</style>

      {toast && (
        <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', background: '#111', border: '1px solid #222', color: '#fff', fontFamily: font.mono, fontSize: '0.7rem', padding: '0.75rem 1.25rem', zIndex: 999, animation: 'fadein 0.2s ease', maxWidth: 360 }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ fontFamily: font.mono, fontSize: '0.58rem', color: '#333', letterSpacing: '0.2em', marginBottom: 6 }}>// MANAGE</div>
          <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '2.5rem', letterSpacing: '-0.02em' }}>Events</div>
          <p style={{ fontFamily: font.body, fontWeight: 300, fontSize: '0.82rem', color: '#444', marginTop: 4 }}>
            {featuredCount}/2 events featured · {events.filter(e => e.is_active).length} active · {events.length} total
          </p>
        </div>
        {!showForm && !editing && (
          <button style={S.btnSave} onClick={() => setShowForm(true)}>
            <Plus size={13} /> ADD EVENT
          </button>
        )}
      </div>

      {/* New event form */}
      {showForm && !editing && (
        <EventForm onSave={handleSave} onCancel={() => setShowForm(false)} saving={saving} />
      )}

      {/* Edit form */}
      {editing && (
        <EventForm initial={editing} onSave={handleSave} onCancel={() => setEditing(null)} saving={saving} />
      )}

      {/* Event list */}
      <div style={S.card}>
        <div style={S.secTitle}>// {events.length} Events</div>

        {loading && (
          <div style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#333', padding: '1rem 0' }}>LOADING...</div>
        )}

        {!loading && events.length === 0 && (
          <div style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#333', padding: '1rem 0' }}>
            No events yet — add one above.
          </div>
        )}

        {!loading && events.map(event => (
          <EventRow
            key={event.id}
            event={event}
            onEdit={e => { setEditing(e); setShowForm(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            onDelete={handleDelete}
            onToggleFeatured={handleToggleFeatured}
            onToggleActive={handleToggleActive}
          />
        ))}
      </div>

      <div style={{ fontFamily: font.mono, fontSize: '0.55rem', color: '#2a2a2a', lineHeight: 2 }}>
        <div>FEATURED → event appears in the homepage hero (max 2 at a time)</div>
        <div>ACTIVE → event is visible on the site; hidden events are removed from public view</div>
        <div>Start Date → used for chronological sorting on the site</div>
      </div>
    </div>
  );
};

export default AdminEvents;
